import type { DropIndicator, EventOverSnapshot } from '../core';

type OverData = { type?: string; blockId?: string; parentId?: string | null };

export type FallbackOverSnapshot = {
  id: string;
  data: { current?: OverData };
};

type BlockNode = { parentBlockId?: string | null };

export const resolveStableId = (
  id: string | null | undefined,
  stableIdByPersistedId: Map<string, string>,
): string | null => {
  if (!id) {
    return null;
  }
  return stableIdByPersistedId.get(id) ?? id;
};

export const isDescendantOf = ({
  candidateStableId,
  ancestorStableId,
  sourceBlockMap,
  stableIdByPersistedId,
}: {
  candidateStableId: string;
  ancestorStableId: string;
  sourceBlockMap: Map<string, BlockNode>;
  stableIdByPersistedId: Map<string, string>;
}): boolean => {
  let cursor: string | null = candidateStableId;
  const visited = new Set<string>();
  while (cursor && !visited.has(cursor)) {
    visited.add(cursor);
    if (cursor === ancestorStableId) {
      return true;
    }
    const node = sourceBlockMap.get(cursor);
    cursor = resolveStableId(node?.parentBlockId ?? null, stableIdByPersistedId);
  }
  return false;
};

export const isFallbackOverStillValidHierarchy = ({
  fallbackOver,
  activeStableId,
  sourceBlockMap,
  stableIdByPersistedId,
}: {
  fallbackOver: FallbackOverSnapshot;
  activeStableId: string | null;
  sourceBlockMap: Map<string, BlockNode>;
  stableIdByPersistedId: Map<string, string>;
}): boolean => {
  if (!activeStableId || !sourceBlockMap.has(activeStableId)) {
    return false;
  }
  const overData = fallbackOver.data.current;
  const overType = overData?.type;
  const overStableId = String(fallbackOver.id);
  const overBlockStableId =
    overType === 'insert-gap-zone' || overType === 'group-gap-zone'
      ? resolveStableId(overData?.blockId, stableIdByPersistedId)
      : overType === 'block'
        ? overStableId
        : null;
  const overParentStableId =
    overType === 'parent-drop' || overType === 'insert-gap-zone' || overType === 'group-gap-zone'
      ? resolveStableId(overData?.parentId ?? null, stableIdByPersistedId)
      : null;

  if (overBlockStableId && !sourceBlockMap.has(overBlockStableId)) {
    return false;
  }
  if (overParentStableId && !sourceBlockMap.has(overParentStableId)) {
    return false;
  }

  if (
    (overBlockStableId &&
      (overBlockStableId === activeStableId ||
        isDescendantOf({
          candidateStableId: overBlockStableId,
          ancestorStableId: activeStableId,
          sourceBlockMap,
          stableIdByPersistedId,
        }))) ||
    (overParentStableId &&
      (overParentStableId === activeStableId ||
        isDescendantOf({
          candidateStableId: overParentStableId,
          ancestorStableId: activeStableId,
          sourceBlockMap,
          stableIdByPersistedId,
        })))
  ) {
    return false;
  }

  return true;
};

export const isLastKnownOverFresh = ({
  now,
  timestampMs,
  maxAgeMs,
}: {
  now: number;
  timestampMs: number | null;
  maxAgeMs: number;
}): boolean => {
  if (timestampMs === null) {
    return false;
  }
  return now - timestampMs <= maxAgeMs;
};

export const resolveEffectiveOverForDragEnd = ({
  eventOver,
  lastKnownOver,
  lastKnownOverTimestampMs,
  now,
  maxLastKnownOverAgeMs,
  latestDropIndicator,
  activeStableId,
  sourceBlockMap,
  stableIdByPersistedId,
  editorBounds,
  dragEndClientPoint,
}: {
  eventOver: EventOverSnapshot | null;
  lastKnownOver: EventOverSnapshot | null;
  lastKnownOverTimestampMs: number | null;
  now: number;
  maxLastKnownOverAgeMs: number;
  latestDropIndicator: DropIndicator | null;
  activeStableId: string | null;
  sourceBlockMap: Map<string, BlockNode>;
  stableIdByPersistedId: Map<string, string>;
  editorBounds: Pick<DOMRect, 'top' | 'right' | 'bottom' | 'left'> | null;
  dragEndClientPoint: { x: number; y: number } | null;
}): EventOverSnapshot | null => {
  if (eventOver) {
    return eventOver;
  }
  if (!lastKnownOver || latestDropIndicator) {
    return null;
  }
  if (
    !isLastKnownOverFresh({
      now,
      timestampMs: lastKnownOverTimestampMs,
      maxAgeMs: maxLastKnownOverAgeMs,
    })
  ) {
    return null;
  }
  if (!editorBounds) {
    return null;
  }
  // Keyboard/programmatic drag-end can have no pointer; allow in-bounds recovery.
  if (
    dragEndClientPoint &&
    (dragEndClientPoint.x < editorBounds.left ||
      dragEndClientPoint.x > editorBounds.right ||
      dragEndClientPoint.y < editorBounds.top ||
      dragEndClientPoint.y > editorBounds.bottom)
  ) {
    return null;
  }
  if (
    !isFallbackOverStillValidHierarchy({
      fallbackOver: {
        id: String(lastKnownOver.id),
        data: {
          current: lastKnownOver.data.current as
            | { type?: string; blockId?: string; parentId?: string | null }
            | undefined,
        },
      },
      activeStableId,
      sourceBlockMap,
      stableIdByPersistedId,
    })
  ) {
    return null;
  }
  return lastKnownOver;
};
